import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { Check, Camera, Thermometer, Droplets } from "lucide-react";
import { MaskLines } from "@/components/Motion";
import { CallButton, ConsultButton } from "@/components/Buttons";
import { TRUST, AREAS_TEXT } from "@/data/site";

const EASE = [0.22, 1, 0.36, 1];

const ReportCard = () => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [10, -10]), { stiffness: 120, damping: 14 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-12, 12]), { stiffness: 120, damping: 14 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => { mx.set(0); my.set(0); };
  const rows = [
    [Check, "Exterior doors & windows", "No visible concern"],
    [Droplets, "Under-sink areas", "Dry"],
    [Thermometer, "Thermostat / Interior", "78°F · 52% RH"],
    [Camera, "Photos attached", "24 images"],
  ];
  return (
    <div className="[perspective:1200px]" onMouseMove={onMove} onMouseLeave={reset}>
      <motion.div
        data-testid="hero-sample-report"
        style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
        initial={{ opacity: 0, y: 60, rotateZ: -4 }}
        animate={{ opacity: 1, y: 0, rotateZ: 0 }}
        transition={{ duration: 1.4, delay: 0.9, ease: EASE }}
        className="w-[320px] rounded-[6px] bg-[#F8F7F3] p-6 text-[#14263D] shadow-[0_50px_100px_-30px_rgba(0,0,0,0.7)]"
      >
        <div className="flex items-center justify-between" style={{ transform: "translateZ(30px)" }}>
          <span className="font-mono text-[0.65rem] uppercase tracking-[0.2em] text-[#A52A32]">Sample Visit Report</span>
          <span className="rounded-full bg-[#14263D] px-2.5 py-1 font-mono text-[0.6rem] text-white">VISIT 03</span>
        </div>
        <p className="mt-3 font-display text-2xl font-semibold" style={{ transform: "translateZ(40px)" }}>Residence · Wellington</p>
        <div className="mt-4 h-28 overflow-hidden rounded-[3px]" style={{ transform: "translateZ(20px)" }}>
          <img src="/images/pool.jpg" alt="Sample report photograph of a backyard pool area" className="h-full w-full object-cover" />
        </div>
        <ul className="mt-4 divide-y divide-[#14263D]/10" style={{ transform: "translateZ(25px)" }}>
          {rows.map(([Icon, k, v]) => (
            <li key={k} className="flex items-center justify-between gap-3 py-2 text-xs">
              <span className="flex items-center gap-2 text-[#14263D]"><Icon className="h-3.5 w-3.5 text-[#A52A32]" />{k}</span>
              <span className="font-mono text-[#5F6B76]">{v}</span>
            </li>
          ))}
        </ul>
        <p className="mt-3 font-mono text-[0.6rem] uppercase tracking-[0.16em] text-[#5F6B76]">Illustrative example only</p>
      </motion.div>
    </div>
  );
};

export const HomeHero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  return (
    <section ref={ref} data-testid="home-hero" className="grain relative min-h-[100svh] overflow-hidden bg-[#14263D] text-white">
      <motion.div
        className="absolute inset-0"
        style={{ y }}
        initial={{ clipPath: "inset(8% 6% 8% 6% round 8px)" }}
        animate={{ clipPath: "inset(0% 0% 0% 0% round 0px)" }}
        transition={{ duration: 1.6, ease: EASE }}
      >
        <motion.img
          src="/images/hero.jpg"
          alt="Upscale white South Florida estate home with manicured topiaries and palm trees"
          className="h-full w-full object-cover"
          style={{ scale }}
          initial={{ filter: "brightness(0.4)" }}
          animate={{ filter: "brightness(1)" }}
          transition={{ duration: 1.8 }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0e1b2c]/95 via-[#14263D]/75 to-[#14263D]/10" />
        <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-[#0e1b2c]/90 to-transparent" />
      </motion.div>

      <div className="relative mx-auto grid min-h-[100svh] max-w-[1400px] items-center gap-10 px-5 pb-36 pt-32 md:px-10 lg:grid-cols-12 lg:pb-32">
        <div className="lg:col-span-8">
          <motion.p className="eyebrow !text-[#f0b3b7]" initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.4, duration: 0.8 }}>
            Home Watch &amp; Concierge · Wellington, Florida
          </motion.p>
          <h1 className="mt-6 font-display text-[2.9rem] font-semibold leading-[0.95] tracking-tight sm:text-7xl lg:text-[6.4rem]" data-testid="hero-headline">
            <MaskLines lines={["Your Home", "Deserves Attention,"]} delay={0.5} />
            <MaskLines lines={["Even When You're Away."]} delay={0.74} lineClassName="italic text-[#f0b3b7]" />
          </h1>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.9, ease: EASE }}>
            <p className="mt-8 max-w-2xl text-base font-medium leading-relaxed text-white md:text-lg">
              Professional Home Watch &amp; Concierge Services founded by a retired Palm Beach County firefighter with 30 years of service.
            </p>
            <p data-testid="hero-based-in" className="mt-3 max-w-2xl text-sm leading-relaxed text-white/80 md:text-base">
              <Link to="/service-areas/wellington" data-testid="hero-wellington-link" className="font-semibold text-white underline decoration-[#f0b3b7] underline-offset-4 hover:text-[#f0b3b7]">Based in Wellington</Link> and proudly serving {AREAS_TEXT.replace("Wellington, ", "")}.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <ConsultButton testId="hero-consult-btn" label="Request Your Personalized Home Watch Quote" dark />
              <CallButton testId="hero-call-btn" dark />
            </div>
          </motion.div>
        </div>
        <div className="hidden justify-end lg:col-span-4 lg:flex">
          <ReportCard />
        </div>
      </div>

      <motion.div
        data-testid="trust-bar"
        className="absolute inset-x-0 bottom-0 border-t border-white/15 bg-[#0e1b2c]/70 backdrop-blur-md"
        initial={{ y: "100%" }}
        animate={{ y: 0 }}
        transition={{ delay: 1.3, duration: 1, ease: EASE }}
      >
        <ul className="mx-auto grid max-w-[1400px] grid-cols-2 px-5 md:grid-cols-4 md:px-10">
          {TRUST.map((t, i) => (
            <li key={t} className={`flex items-center gap-3 py-4 font-mono text-[0.7rem] uppercase tracking-[0.16em] text-white/90 md:py-5 ${i ? "md:border-l md:border-white/10 md:pl-6" : ""}`}>
              <span className="h-1.5 w-1.5 rounded-full bg-[#A52A32]" />{t}
            </li>
          ))}
        </ul>
      </motion.div>
    </section>
  );
};
